/**
 * 원격지원 파일 주고받기 — 직원 PC 쪽(메인 창, PC 앱에서만 마운트)
 *
 *  - 담당자가 보낸 파일: 동의 모달 '파일 받기'(창을 앞으로) → [받기] → 서버 허가(rs:file:start) → 다운로드 폴더(Rust)
 *  - 담당자의 파일 요청: 동의 모달 '파일 요청' → [파일 선택](클릭 안에서 선택 창) → 서버 허가 → 담당자에게 전송
 *  - 넓히는 쪽(이 PC에 파일이 들어오거나 이 PC의 파일이 나가는 것)은 언제나 직원 클릭으로만 시작한다.
 *    담당자가 제어 중이면 담당자가 대신 누를 수도 있다(제어 = 직원 계정 대행에 이미 동의했다)
 *  - 묶음이 끝나면 결과를 서버에 보고한다(rs:file:result → 감사 로그). 데이터는 P2P라 서버가 보지 않는다
 *  - 저장만 하고 실행하지 않는다. Rust가 Mark-of-the-Web을 남겨 SmartScreen·Office 보호된 보기를 거친다
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, FolderOpen, Upload, X } from 'lucide-react';
import { Modal } from '../Modal';
import { useToast } from '../../contexts/ToastContext';
import { socketService } from '../../services/socketService';
import { rsInvoke, useRemoteSupportMe } from '../../services/remoteSupportService';
import { FxBatchState, FxSnapshot, FxSource, RsFileTransfer } from '../../services/remoteFileTransfer';
import { openRustSink, revealReceivedFile } from '../../services/remoteFileSinks';
import {
  FX_CONSENT_MS,
  FX_MAX_FILES,
  checkOutgoing,
  fxReasonLabel,
  percentOf,
  summarizeFiles,
} from '../../utils/remoteFileProtocol';
import { formatFileSize } from '../../utils/formatFileSize';

interface Props {
  /** RsPeer가 넘겨준 'fx' 채널. 세션이 끝나면 null */
  channel: RTCDataChannel | null;
  sessionId: string | null;
  operatorName: string;
}

interface OfferAsk {
  batch: FxBatchState;
  resolve: (accept: boolean) => void;
  expiresAt: number;
}

interface PullAsk {
  pid: number;
  resolve: (files: FxSource[] | null) => void;
  expiresAt: number;
}

/** 서버에 남기는 묶음 키 — 받는 묶음은 상대가 만든 bid, 보내는 묶음은 담당자의 요청 번호 */
const tidOf = (b: { direction: 'in' | 'out'; bid: number; pullId?: number }) =>
  b.direction === 'in' ? `in-${b.bid}` : `pull-${b.pullId ?? b.bid}`;

const NAMES_SHOWN = 5;

export const RemoteFileHost: React.FC<Props> = ({ channel, sessionId, operatorName }) => {
  const { showToast } = useToast();
  const me = useRemoteSupportMe();
  const [snap, setSnap] = useState<FxSnapshot | null>(null);
  const [offerAsk, setOfferAsk] = useState<OfferAsk | null>(null);
  const [pullAsk, setPullAsk] = useState<PullAsk | null>(null);
  const [now, setNow] = useState(Date.now());
  const engineRef = useRef<RsFileTransfer | null>(null);
  const offerRef = useRef<OfferAsk | null>(null);
  const pullRef = useRef<PullAsk | null>(null);
  const policyRef = useRef(me.fileTransferEnabled);
  const operatorRef = useRef(operatorName);
  const inputRef = useRef<HTMLInputElement>(null);
  /** 서버가 허가한 묶음 — 이것만 결과를 보고한다(허가 전에 거절된 것은 접근이 없었다) */
  const startedRef = useRef(new Set<string>());

  useEffect(() => { operatorRef.current = operatorName; }, [operatorName]);

  const setOffer = (v: OfferAsk | null) => {
    offerRef.current = v;
    setOfferAsk(v);
  };
  const setPull = (v: PullAsk | null) => {
    pullRef.current = v;
    setPullAsk(v);
  };

  /** 떠 있는 동의 창을 거절로 닫는다(세션 종료·정책 꺼짐·만료) */
  const closeAsks = useCallback(() => {
    offerRef.current?.resolve(false);
    setOffer(null);
    pullRef.current?.resolve(null);
    setPull(null);
  }, []);

  /** 서버 허가 — 정책·세션 상태·상한을 서버가 최종 판정한다 */
  const serverStart = useCallback(
    async (tid: string, direction: 'to_host' | 'to_operator', files: { name: string; size: number }[]) => {
      if (!sessionId) return false;
      const ack = await socketService.emitRemoteSupportWithAck<{ ok: boolean; message?: string }>('rs:file:start', {
        sessionId,
        tid,
        direction,
        files: files.map((f) => ({ name: f.name, size: f.size })),
      });
      if (!ack.ok) {
        showToast({ type: 'error', message: ack.message || '파일을 주고받지 못했습니다. 잠시 후 다시 시도해 주세요.' });
        return false;
      }
      startedRef.current.add(tid);
      return true;
    },
    [sessionId, showToast]
  );

  // 엔진 — 채널이 생기면 붙이고, 세션이 끝나면(채널 null) 버린다(받던 부분 파일은 싱크 abort + Rust가 지운다)
  useEffect(() => {
    if (!channel || !sessionId) return;
    const started = startedRef.current;
    const bringToFront = () => rsInvoke('rs_bring_to_front').catch(() => {});
    const engine = new RsFileTransfer({
      role: 'host',
      channel,
      openSink: (file) => openRustSink(file, sessionId),
      decideOffer: async (batch) => {
        if (!policyRef.current || offerRef.current) return false;
        bringToFront();
        const accept = await new Promise<boolean>((resolve) => {
          setNow(Date.now());
          setOffer({ batch, resolve, expiresAt: Date.now() + FX_CONSENT_MS });
        });
        setOffer(null);
        if (!accept || !policyRef.current) return false;
        return serverStart(tidOf(batch), 'to_host', batch.files);
      },
      decidePull: async (pid) => {
        if (!policyRef.current || pullRef.current) return null;
        bringToFront();
        const files = await new Promise<FxSource[] | null>((resolve) => {
          setNow(Date.now());
          setPull({ pid, resolve, expiresAt: Date.now() + FX_CONSENT_MS });
        });
        setPull(null);
        if (!files || !policyRef.current) return null;
        const ok = await serverStart(`pull-${pid}`, 'to_operator', files);
        return ok ? files : null;
      },
      onChange: setSnap,
      onBatchEnd: (b) => {
        const tid = tidOf(b);
        if (b.direction === 'in' && b.status === 'done') {
          const names = summarizeFiles(b.files.map((f) => ({ name: f.savedAs || f.name })));
          showToast({ type: 'success', message: `다운로드 폴더에 저장했습니다 — ${names}` });
        }
        if (!startedRef.current.has(tid)) return;
        startedRef.current.delete(tid);
        socketService.emitRemoteSupport('rs:file:result', {
          sessionId,
          tid,
          status: b.status,
          reason: b.reason ?? null,
          files: b.files.map((f) => ({ name: f.savedAs || f.name, size: f.size, status: f.status })),
        });
      },
    });
    engineRef.current = engine;
    setSnap(engine.snapshot());
    return () => {
      engine.dispose('disconnected');
      engineRef.current = null;
      closeAsks();
      started.clear();
      setSnap(null);
    };
  }, [channel, sessionId, serverStart, closeAsks, showToast]);

  // 관리자가 끄면 진행 중인 것을 멈추고 떠 있는 동의 창을 닫는다
  useEffect(() => {
    policyRef.current = me.fileTransferEnabled;
    if (me.fileTransferEnabled) return;
    const e = engineRef.current;
    const busy = !!offerRef.current || !!pullRef.current || !!e?.snapshot().batches.some((b) => b.status === 'active' || b.status === 'waiting');
    closeAsks();
    e?.cancelAll('policy_off');
    if (busy) showToast({ type: 'info', message: '관리자가 파일 주고받기를 막았습니다.' });
  }, [me.fileTransferEnabled, closeAsks, showToast]);

  // 동의 카운트다운 — 끝나면 거절
  useEffect(() => {
    if (!offerAsk && !pullAsk) return;
    const t = window.setInterval(() => {
      const n = Date.now();
      setNow(n);
      if (offerRef.current && n >= offerRef.current.expiresAt) {
        offerRef.current.resolve(false);
        setOffer(null);
      }
      if (pullRef.current && n >= pullRef.current.expiresAt) {
        pullRef.current.resolve(null);
        setPull(null);
      }
    }, 1000);
    return () => window.clearInterval(t);
  }, [offerAsk, pullAsk]);

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files ?? []);
    e.target.value = '';
    const ask = pullRef.current;
    if (!ask || list.length === 0) return;
    const check = checkOutgoing(list);
    if (check.ok === false) {
      showToast({
        type: 'error',
        message:
          check.reason === 'too_many'
            ? `한 번에 ${FX_MAX_FILES}개까지 보낼 수 있습니다.`
            : check.reason === 'too_large'
              ? '2GB가 넘는 파일은 보낼 수 없습니다.'
              : '보낼 수 없는 파일입니다.',
      });
      return; // 창은 그대로 — 다시 고르거나 거절한다
    }
    ask.resolve(list);
  };

  const reveal = (revealId?: string) => {
    if (!revealId) return;
    revealReceivedFile(revealId).catch(() =>
      showToast({ type: 'error', message: '폴더를 열지 못했습니다. 다운로드 폴더에서 직접 찾아 주세요.' })
    );
  };

  const remain = (expiresAt: number) => Math.max(0, Math.ceil((expiresAt - now) / 1000));
  const visible = (snap?.batches ?? []).slice(-3);
  const offerFiles = offerAsk?.batch.files ?? [];
  const opName = operatorRef.current || '담당자';

  return (
    <>
      <input ref={inputRef} type="file" multiple className="hidden" onChange={onPickFiles} tabIndex={-1} aria-hidden="true" />

      {/* 담당자가 보낸 파일 — 닫기(X·ESC)는 거절 */}
      <Modal
        isOpen={!!offerAsk}
        onClose={() => offerRef.current?.resolve(false)}
        title="파일 받기"
        size="sm"
        closeOnOverlay={false}
        footer={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => offerRef.current?.resolve(false)}
              className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
            >
              거절
            </button>
            <button
              type="button"
              onClick={() => offerRef.current?.resolve(true)}
              className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-brand-600 hover:bg-brand-700 text-white transition-colors"
            >
              받기 ({offerAsk ? remain(offerAsk.expiresAt) : 0})
            </button>
          </div>
        }
      >
        {offerAsk && (
          <div className="space-y-3 text-sm">
            <p className="text-slate-700 leading-relaxed">
              {opName}님이 파일을 보냅니다. 받으면 <b>다운로드 폴더</b>에 저장됩니다.
            </p>
            <ul className="rounded-lg border border-slate-200 divide-y divide-slate-100">
              {offerFiles.slice(0, NAMES_SHOWN).map((f) => (
                <li key={f.fid} className="flex items-center gap-2 px-3 py-2">
                  <Download size={14} className="text-slate-400 shrink-0" aria-hidden="true" />
                  <span className="flex-1 min-w-0 truncate text-slate-800" title={f.name}>{f.name}</span>
                  <span className="shrink-0 text-xs text-slate-500 tabular-nums">{formatFileSize(f.size)}</span>
                </li>
              ))}
              {offerFiles.length > NAMES_SHOWN && (
                <li className="px-3 py-2 text-xs text-slate-500">
                  외 {offerFiles.length - NAMES_SHOWN}개 · 합계 {formatFileSize(offerAsk.batch.totalBytes)}
                </li>
              )}
            </ul>
            <p className="text-xs text-slate-500">저장만 하고 실행하지 않습니다. 실행은 직접 해야 합니다.</p>
          </div>
        )}
      </Modal>

      {/* 담당자의 파일 요청 — 고른 파일만 나간다. 선택 창을 취소하면 이 창은 그대로 남는다 */}
      <Modal
        isOpen={!!pullAsk}
        onClose={() => pullRef.current?.resolve(null)}
        title="파일 요청"
        size="sm"
        closeOnOverlay={false}
        footer={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => pullRef.current?.resolve(null)}
              className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
            >
              거절
            </button>
            <button
              type="button"
              onClick={() => {
                // 선택 창은 클릭 안에서만 열린다. 고르는 동안 만료되지 않게 시간을 넉넉히 늘린다
                const ask = pullRef.current;
                if (ask) setPull({ ...ask, expiresAt: Date.now() + 2 * FX_CONSENT_MS });
                inputRef.current?.click();
              }}
              className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-brand-600 hover:bg-brand-700 text-white transition-colors"
            >
              파일 선택 ({pullAsk ? remain(pullAsk.expiresAt) : 0})
            </button>
          </div>
        }
      >
        <div className="space-y-2 text-sm">
          <p className="text-slate-700 leading-relaxed">
            {opName}님이 이 PC의 파일을 요청합니다. 고른 파일만 담당자에게 전송됩니다.
          </p>
          <p className="text-xs text-slate-500">한 번에 {FX_MAX_FILES}개, 파일당 2GB까지 보낼 수 있습니다.</p>
        </div>
      </Modal>

      {/* 진행 카드 — 끝난 묶음은 닫을 때까지 남는다([폴더 열기]) */}
      {visible.length > 0 && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-6 right-6 z-50 w-[min(22rem,calc(100vw-2rem))] space-y-2"
        >
          {visible.map((b) => {
            const pct = percentOf(b.doneBytes, b.totalBytes);
            const ended = b.status !== 'active' && b.status !== 'waiting';
            const isIn = b.direction === 'in';
            const names = summarizeFiles(b.files.map((f) => ({ name: f.savedAs || f.name })));
            return (
              <div key={`${b.direction}-${b.bid}`} className="rounded-2xl bg-white border border-slate-200 shadow-xl p-3">
                <div className="flex items-start gap-2">
                  {isIn ? (
                    <Download size={16} className="mt-0.5 text-slate-500 shrink-0" aria-hidden="true" />
                  ) : (
                    <Upload size={16} className="mt-0.5 text-slate-500 shrink-0" aria-hidden="true" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-800 truncate" title={names}>
                      {names || (isIn ? '받는 파일' : '보내는 파일')}
                    </p>
                    <p className="text-xs text-slate-500">
                      {b.status === 'active' &&
                        `${isIn ? '받는 중' : '보내는 중'} · ${pct}% · ${formatFileSize(b.doneBytes)} / ${formatFileSize(b.totalBytes)}`}
                      {b.status === 'waiting' && (isIn ? '받을지 고르는 중' : '담당자에게 보내는 중')}
                      {b.status === 'done' && (isIn ? '다운로드 폴더에 저장했습니다' : '담당자에게 보냈습니다')}
                      {(b.status === 'declined' || b.status === 'canceled' || b.status === 'failed') && fxReasonLabel(b.reason)}
                    </p>
                  </div>
                  {ended ? (
                    <button
                      type="button"
                      onClick={() => engineRef.current?.dismiss(b.bid)}
                      className="shrink-0 p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                      aria-label="닫기"
                    >
                      <X size={14} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => engineRef.current?.cancel(b.bid)}
                      className="shrink-0 px-2 py-1 rounded-md border border-slate-300 text-xs text-slate-700 hover:bg-slate-50"
                    >
                      취소
                    </button>
                  )}
                </div>
                {b.status === 'active' && (
                  <div className="mt-2 h-2 rounded-full bg-slate-200 overflow-hidden" aria-hidden="true">
                    <div className="h-full bg-brand-600 transition-[width] duration-200" style={{ width: `${pct}%` }} />
                  </div>
                )}
                {isIn && b.status === 'done' && b.files.some((f) => f.revealId) && (
                  <button
                    type="button"
                    onClick={() => reveal(b.files.find((f) => f.revealId)?.revealId)}
                    className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    <FolderOpen size={14} aria-hidden="true" />
                    폴더 열기
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};
